import type { HTMLAttributes, ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";

const tones = {
  info: { cls: "bg-brand-50 text-brand-900 ring-brand-600/15", Icon: Info },
  success: { cls: "bg-success-soft/70 text-green-900 ring-green-700/15", Icon: CheckCircle2 },
  warning: { cls: "bg-warning-soft/70 text-amber-900 ring-amber-600/20", Icon: AlertTriangle },
  danger: { cls: "bg-danger-soft/70 text-red-900 ring-red-700/15", Icon: XCircle },
} as const;

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: keyof typeof tones;
  title?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}

export function Alert({ tone = "info", title, action, icon, className, children, ...props }: AlertProps) {
  const { cls, Icon } = tones[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex gap-3 rounded-2xl p-4 text-sm ring-1 ring-inset", cls, className)} {...props}>
      <span className="mt-0.5 shrink-0 [&_svg]:size-[18px]">{icon ?? <Icon aria-hidden="true" />}</span>
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="leading-relaxed opacity-90">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}
